import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { updateAddress, deleteAddress, createAddress } from '../../redux/doctorSlice'; // Adjust the import path as needed
import empty3 from '../../assets/img/colorkit (3).svg';
import { Plus, X, Trash2, Edit, Search } from 'lucide-react';
import CountryFlag from '../common/CountryFlag';
import MasterLabel from '../common/MasterLabel';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { sanitizePostalCode, postalCodePattern, POSTAL_CODE_MAX_LENGTH } from '../../utils/personFields';

// Translated specialty name used inside react-select's formatOptionLabel renderer.
// `option.value` holds the specialty ID, `option.label` holds the English fallback.
const SpecialtyOptionLabel = ({ option }) => {
    const label = useMasterLabel('masterSpecialty', option?.value, 'name', option?.label || '');
    return <>{label}</>;
};

function AddressTab() {
    const { selectedDoctor, loading, error } = useSelector((state) => state.doctor);
    const dispatch = useDispatch();
    const userRole = localStorage.getItem('Role');
    const language = localStorage.getItem('i18nextLng') || 'en';
    const doctorId = userRole === 'admin' ? selectedDoctor.doctorId : userRole === 'doctor' ? localStorage.getItem('ID') : '';
    const { t } = useTranslation(['doctoraddress']);
    const scrollContainerRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        addressId: '',
        addressType: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        zipCode: '',
        specialty: [],
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [charLimitReached, setCharLimitReached] = useState({});
    const [isSpecialtyMenuOpen, setIsSpecialtyMenuOpen] = useState(false);
    const [isSpecialtyInputFocused, setIsSpecialtyInputFocused] = useState(false);

    const alladdress = selectedDoctor?.address || [];
    const doctorSelected = selectedDoctor?.appointmentSpeciality || [];
    const doctorCountry = selectedDoctor?.country;
    const countryName = doctorCountry ? (typeof doctorCountry === 'object' ? (doctorCountry.countryName || doctorCountry.countryname || '') : doctorCountry) : '';
    const zipMinLength = typeof doctorCountry === 'object' ? (doctorCountry.zipMinLength || 5) : 5;
    const zipMaxLength = typeof doctorCountry === 'object' ? (doctorCountry.zipMaxLength || 6) : 6;

    const mappedSpecialties = doctorSelected.map((specialty) => {
        const hasTypes = Array.isArray(specialty.appointmentTypes) && specialty.appointmentTypes.length > 0;

        return {
            value: specialty.specialtyId,
            label: specialty.name,
            color: specialty.color,
            specialtyId: specialty.specialtyId,
            name: specialty.name,
            hasTypes,
        };
    });

    // Reset form when modal closes or opens for adding new address
    const resetForm = () => {
        setFormData({
            addressId: '',
            addressType: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            zipCode: '',
            specialty: [],
        });
        setFormErrors({});
        setCharLimitReached({});
    };

    const handleAddClick = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setIsSubmitting(false);
        resetForm();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = { addressType: 20, addressLine1: 100, addressLine2: 100, city: 25, zipCode: zipMaxLength };

        // Postal code: letters and digits only, capped at the shared max (10);
        // the country master narrows the length further via zipMaxLength.
        if (name === 'zipCode') {
            const sanitized = sanitizePostalCode(value);
            setFormData((prev) => ({ ...prev, [name]: sanitized }));
            setFormErrors((prev) => ({ ...prev, [name]: '' }));

            // Check if character limit is reached
            if (maxLengths[name] && sanitized.length >= maxLengths[name]) {
                setCharLimitReached({ ...charLimitReached, [name]: true });
            } else {
                setCharLimitReached({ ...charLimitReached, [name]: false });
            }
            return;
        }

        // For text fields, allow input but sanitize special characters
        if (name === 'addressType' || name === 'addressLine1' || name === 'addressLine2' || name === 'city') {
            // Remove any potentially harmful characters but allow most text
            // Allow letters (including accented), numbers, spaces, common punctuation, and newlines
            let sanitizedValue = value.replace(/[<>{}[\]\\]/g, '');
            // Auto uppercase for address title
            if (name === 'addressType') sanitizedValue = sanitizedValue.toUpperCase();
            setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));

            // Check for whitespace-only values for required fields
            if ((name === 'addressType' || name === 'addressLine1' || name === 'city') && sanitizedValue.trim().length === 0 && sanitizedValue.length > 0) {
                setFormErrors((prev) => ({ ...prev, [name]: t('doctoraddress.required') }));
            } else {
                // Real-time duplicate validation for addressType
                if (name === 'addressType') {
                    const isDuplicate = alladdress.some(addr =>
                        addr.addressType.toLowerCase() === sanitizedValue.trim().toLowerCase() &&
                        addr.addressId !== formData.addressId
                    );
                    if (isDuplicate) {
                        setFormErrors((prev) => ({ ...prev, [name]: t('doctoraddress.duplicateTitle') }));
                    } else {
                        setFormErrors((prev) => ({ ...prev, [name]: '' }));
                    }
                } else {
                    setFormErrors((prev) => ({ ...prev, [name]: '' }));
                }
            }

            // Check if character limit is reached
            if (maxLengths[name] && sanitizedValue.length >= maxLengths[name]) {
                setCharLimitReached({ ...charLimitReached, [name]: true });
            } else {
                setCharLimitReached({ ...charLimitReached, [name]: false });
            }
        } else {
            // For other fields (like country dropdown)
            setFormData((prev) => ({ ...prev, [name]: value }));
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSpecialityChange = (selectedOptions) => {
        setFormData((prev) => ({
            ...prev,
            specialty: selectedOptions
                ? selectedOptions.map((option) => ({
                    specialtyId: option.specialtyId,
                    name: option.name,
                    color: option.color,
                }))
                : [],
        }));
        // Clear error on change
        setFormErrors((prev) => ({ ...prev, specialty: '' }));
    };

    // Handler to filter specialty search input - only allow letters and spaces
    const handleSpecialtyInputChange = (inputValue, { action }) => {
        // Allow only letters (including accented characters) and spaces
        // Remove numbers and special characters
        if (action === 'input-change') {
            let filteredValue = inputValue.replace(/[^a-zA-Z\u00C0-\u017F\s]/g, '');
            // Enforce max length of 50 characters
            if (filteredValue.length > 50) {
                filteredValue = filteredValue.substring(0, 50);
            }
            return filteredValue;
        }
        return inputValue;
    };

    // Function to scroll to bottom when specialty select is opened
    const handleSpecialtyMenuOpen = () => {
        if (scrollContainerRef.current) {
            setTimeout(() => {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 100);
        }
    };

    const validateForm = () => {
        const errors = {};
        // Title validation (max 20 characters)
        if (!formData.addressType.trim()) {
            errors.addressType = t('doctoraddress.required');
        } else if (formData.addressType.length > 20) {
            errors.addressType = t('doctoraddress.titleMaxLength');
        } else if (alladdress.some(addr =>
            addr.addressType.toLowerCase() === formData.addressType.trim().toLowerCase() &&
            addr.addressId !== formData.addressId
        )) {
            errors.addressType = t('doctoraddress.duplicateTitle');
        }

        // Address Line 1 validation (max 100 characters)
        if (!formData.addressLine1.trim()) {
            errors.addressLine1 = t('doctoraddress.required');
        } else if (formData.addressLine1.length > 100) {
            errors.addressLine1 = t('doctoraddress.addressMaxLength');
        }

        // Address Line 2 validation (max 100 characters, optional)
        if (formData.addressLine2 && formData.addressLine2.length > 100) {
            errors.addressLine2 = t('doctoraddress.additionalMaxLength');
        }

        // City validation (max 25 characters)
        if (!formData.city.trim()) {
            errors.city = t('doctoraddress.required');
        } else if (formData.city.length > 25) {
            errors.city = t('doctoraddress.cityMaxLength');
        }

        // Zip code: required, alphanumeric, length driven by the country master
        // (clamped to the shared max of 10 in personFields).
        if (!formData.zipCode.trim()) {
            errors.zipCode = t('doctoraddress.required');
        } else if (zipMinLength === zipMaxLength && !postalCodePattern(zipMinLength, zipMaxLength).test(formData.zipCode.trim())) {
            errors.zipCode = t('doctoraddress.invalidZipcode', { length: zipMinLength });
        } else if (zipMinLength !== zipMaxLength && !postalCodePattern(zipMinLength, zipMaxLength).test(formData.zipCode.trim())) {
            errors.zipCode = t('doctoraddress.invalidZipcodeRange', { min: zipMinLength, max: zipMaxLength });
        }

        // Specialty validation
        if (formData.specialty.length === 0) {
            errors.specialty = t('doctoraddress.requiredSpecialty');
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const isEdit = !!formData.addressId;

        const result = await Swal.fire({
            title: isEdit
                ? t('doctoraddress.confirm_update_title')
                : t('doctoraddress.confirm_create_title'),
            text: isEdit
                ? t('doctoraddress.confirm_update_text')
                : t('doctoraddress.confirm_create_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: isEdit
                ? t('doctoraddress.yes_update')
                : t('doctoraddress.yes_create'),
            cancelButtonText: t('doctoraddress.cancel'),
        });

        if (!result.isConfirmed) return;

        setIsSubmitting(true);
        try {
            if (isEdit) {
                const payload = {
                    doctorId,
                    addressData: {
                        addressId: formData.addressId,
                        addressType: formData.addressType,
                        addressLine1: formData.addressLine1,
                        addressLine2: formData.addressLine2,
                        city: formData.city,
                        country: countryName,
                        zipCode: formData.zipCode,
                        specialty: formData.specialty,
                    },
                };
                await dispatch(updateAddress(payload)).unwrap();

                toast.success(t('doctoraddress.success_update_text'));
            } else {
                const payload = {
                    doctorId,
                    addressData: {
                        addressType: formData.addressType,
                        addressLine1: formData.addressLine1,
                        addressLine2: formData.addressLine2,
                        city: formData.city,
                        country: countryName,
                        zipCode: formData.zipCode,
                        specialty: formData.specialty,
                    },
                };
                await dispatch(createAddress(payload)).unwrap();

                toast.success(t('doctoraddress.success_create_text'));
            }

            handleModalClose();
        } catch (err) {
            toast.warn(err?.error?.response?.data?.message?.[language] || t('doctoraddress.error_save_text'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditAddress = (address) => {
        setFormData({
            addressId: address.addressId,
            addressType: address.addressType,
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            city: address.city,
            zipCode: address.zipCode,
            specialty: address.specialty.map((spec) => ({
                specialtyId: spec.specialtyId,
                name: spec.name,
                color: spec.color,
            })),
        });
        setFormErrors({});
        setIsModalOpen(true);
    };

    const handleDeleteAddress = async (addressId) => {
        const result = await Swal.fire({
            title: t('doctoraddress.deleteConfirmTitle'),
            text: t('doctoraddress.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('doctoraddress.deleteConfirmYes'),
            cancelButtonText: t('doctoraddress.deleteConfirmNo'),
        });

        if (!result.isConfirmed) return;

        try {
            await dispatch(
                deleteAddress({ addressId, docId: doctorId })
            ).unwrap();

            toast.success(t('doctoraddress.successDeleteText'));
        } catch (err) {
            toast.warn(err?.error?.response?.data?.message?.[language] || t('doctoraddress.errorDeleteText'));
        }
    };

    // Custom styles for react-select to match theme
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: '#d1d5db',
            padding: '0.25rem',
            borderRadius: '0.375rem',
            boxShadow: 'none',
            '&:hover': { borderColor: '#4ABDBD' },
        }),
        multiValue: (provided, state) => ({
            ...provided,
            backgroundColor: state.data.color,
            color: '#fff',
            borderRadius: '9999px',
            paddingLeft: '6px',
            paddingRight: '4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#fff',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#fff',
            '&:hover': {
                backgroundColor: '#000',
                color: '#fff',
            },
        }),
    };

    return (
        <div data-testid="address-tab-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="address-tab-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="address-tab-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('doctoraddress.heading')}
                </h2>
                <button
                    onClick={handleAddClick}
                    className="group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm"
                    disabled={loading}
                    data-testid="add-address-button"
                >
                    <Plus className="w-5 h-5" />
                    <span data-testid="add-address-text">{t('doctoraddress.addaddress')}</span>
                </button>
            </div>

            {/* Address Cards or Empty State */}
            {
                alladdress.length === 0 ? (
                    <div data-testid="address-tab-empty" className="flex flex-col items-center justify-center w-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                        <img
                            src={empty3}
                            alt={t('doctoraddress.no_active_specialties_found', 'No Active Specialties Found')}
                            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-52 lg:h-52 object-contain"
                            data-testid="no-specialties-image"
                        />
                        <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg" data-testid="no-specialties-text">
                            {t('doctoraddress.noAddresses')}
                        </p>
                    </div>
                ) : (
                    <div data-testid="address-tab-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                        {alladdress.map((addr) => (
                            <div
                                key={addr.addressId}
                                className="group relative flex items-start gap-4 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-5"
                                data-testid={`address-card-${addr.addressId}`}
                            >
                                {/* Flag */}
                                <div data-testid={`address-card-${addr.addressId}-flag-wrap`} className="absolute bottom-2 right-3">
                                    <CountryFlag
                                        countryName={countryName}
                                        alt={t('doctoraddress.flag_alt', 'Flag')}
                                        className="w-8 h-8  object-cover"
                                    />
                                </div>

                                {/* Address Info */}
                                <div data-testid={`address-card-${addr.addressId}-info`} className="flex-1 min-w-0 space-y-2">
                                    <p data-testid={`address-card-${addr.addressId}-type`} className="text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors duration-300 flex items-center gap-2">
                                        {addr.addressType}
                                    </p>
                                    <p data-testid={`address-card-${addr.addressId}-line1`} className="text-gray-600 text-xs">{addr.addressLine1}</p>
                                    {addr.addressLine2 && (
                                        <p data-testid={`address-card-${addr.addressId}-line2`} className="text-gray-600 text-xs">{addr.addressLine2}</p>
                                    )}
                                    <p data-testid={`address-card-${addr.addressId}-citycountry`} className="text-gray-600 text-xs">
                                        {addr.city}, <MasterLabel type="masterCountry" id={typeof addr.country === 'object' ? addr.country?.countryId : null} field="countryName" fallback={typeof addr.country === 'object' ? (addr.country?.countryName || '') : (addr.country || '')} /> {addr.zipCode}
                                    </p>
                                    <div data-testid={`address-card-${addr.addressId}-specialty-row`} className="flex flex-wrap items-center gap-1.5 mt-2">
                                        <span data-testid={`address-card-${addr.addressId}-specialty-label`} className="font-semibold text-gray-800 text-xs">
                                            {t("doctoraddress.specialities", { ns: "doctoraddress" })}:
                                        </span>
                                        {addr.specialty.map((spec, specIdx) => (
                                            <span
                                                key={specIdx}
                                                data-testid={`address-card-${addr.addressId}-specialty-chip-${spec.specialtyId || specIdx}`}
                                                className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white"
                                                style={{ backgroundColor: spec.color || '#1a2e35' }}
                                            >
                                                <MasterLabel type="masterSpecialty" id={spec.specialtyId} field="name" fallback={spec.name} />
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-all duration-300">
                                    <button
                                        onClick={() => handleEditAddress(addr)}
                                        disabled={loading}
                                        data-testid={`edit-address-button-${addr.addressId}`}
                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteAddress(addr.addressId)}
                                        disabled={loading}
                                        data-testid={`delete-address-button-${addr.addressId}`}
                                        className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}

                    </div>
                )}

            {/* Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="doctor-address-modal-title"
                    id="doctor-address-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="doctor-address-modal-title"
                                data-testid="modal-title"
                            >
                                {formData.addressId
                                    ? t('doctoraddress.editaddress')
                                    : t('doctoraddress.addaddress')}
                            </h2>
                            <button
                                id="doctor-address-modal-close-button"
                                onClick={handleModalClose}
                                aria-label={t('doctoraddress.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                disabled={isSubmitting}
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="doctor-address-form" onSubmit={handleSubmit} data-testid="address-form" className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="address-type-input"
                                            id="address-type-label"
                                            data-testid="address-type-label"
                                        >
                                            {t('doctoraddress.title')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="address-type-input"
                                            name="addressType"
                                            value={formData.addressType}
                                            onChange={handleInputChange}
                                            placeholder={t('doctoraddress.titlePlaceholder')}
                                            maxLength={20}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${formErrors.addressType ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            disabled={isSubmitting}
                                            data-testid="address-type-input"
                                        />
                                        {formErrors.addressType && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="address-type-error"
                                                data-testid="address-type-error"
                                            >
                                                {formErrors.addressType}
                                            </p>
                                        )}
                                        {charLimitReached.addressType && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctoraddress.reached_text_limit', { limit: 20 })}</p>}
                                    </div>
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="address-line1-input"
                                            id="address-line1-label"
                                            data-testid="address-line1-label"
                                        >
                                            {t('doctoraddress.address')} <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="address-line1-input"
                                            name="addressLine1"
                                            value={formData.addressLine1}
                                            onChange={handleInputChange}
                                            placeholder={t('doctoraddress.addressPlaceholder')}
                                            maxLength={100}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${formErrors.addressLine1 ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            rows="3"
                                            disabled={isSubmitting}
                                            data-testid="address-line1-input"
                                        />
                                        {formErrors.addressLine1 && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="address-line1-error"
                                                data-testid="address-line1-error"
                                            >
                                                {formErrors.addressLine1}
                                            </p>
                                        )}
                                        {charLimitReached.addressLine1 && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctoraddress.reached_text_limit', { limit: 100 })}</p>}
                                    </div>
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="address-line2-input"
                                            id="address-line2-label"
                                            data-testid="address-line2-label"
                                        >
                                            {t('doctoraddress.additional')}
                                        </label>
                                        <textarea
                                            id="address-line2-input"
                                            name="addressLine2"
                                            value={formData.addressLine2}
                                            onChange={handleInputChange}
                                            placeholder={t('doctoraddress.additionalPlaceholder')}
                                            maxLength={100}
                                            className="w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 border-gray-300 hover:border-[#4ABDBD]"
                                            rows="3"
                                            disabled={isSubmitting}
                                            data-testid="address-line2-input"
                                        />
                                        {formErrors.addressLine2 && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="address-line2-error"
                                                data-testid="address-line2-error"
                                            >
                                                {formErrors.addressLine2}
                                            </p>
                                        )}
                                        {charLimitReached.addressLine2 && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctoraddress.reached_text_limit', { limit: 100 })}</p>}
                                    </div>
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="city-input"
                                            id="city-label"
                                            data-testid="city-label"
                                        >
                                            {t('doctoraddress.city')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="city-input"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            placeholder={t('doctoraddress.cityPlaceholder')}
                                            maxLength={25}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${formErrors.city ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            disabled={isSubmitting}
                                            data-testid="city-input"
                                        />
                                        {formErrors.city && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="city-error"
                                                data-testid="city-error"
                                            >
                                                {formErrors.city}
                                            </p>
                                        )}
                                        {charLimitReached.city && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctoraddress.reached_text_limit', { limit: 25 })}</p>}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            id="country-label"
                                            data-testid="country-label"
                                        >
                                            {t('doctoraddress.country')}
                                        </label>
                                        <div className="flex items-center gap-2 w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm text-gray-700" data-testid="country-display">
                                            <CountryFlag countryName={countryName} />
                                            <span>{countryName || '-'}</span>
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500 italic">
                                            {t('doctoraddress.country_auto_set', 'Country is automatically set based on your country')}
                                        </p>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="zipcode-input"
                                            id="zipcode-label"
                                            data-testid="zipcode-label"
                                        >
                                            {t('doctoraddress.zipcode')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="zipcode-input"
                                            name="zipCode"
                                            value={formData.zipCode}
                                            onChange={handleInputChange}
                                            placeholder={zipMinLength === zipMaxLength
                                                ? t('doctoraddress.zipcodePlaceholder', { length: zipMinLength })
                                                : t('doctoraddress.zipcodePlaceholderRange', { min: zipMinLength, max: zipMaxLength })}
                                            maxLength={Math.min(zipMaxLength, POSTAL_CODE_MAX_LENGTH)}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${formErrors.zipCode ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            disabled={isSubmitting}
                                            data-testid="zipcode-input"
                                        />
                                        {formErrors.zipCode && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="zipcode-error"
                                                data-testid="zipcode-error"
                                            >
                                                {formErrors.zipCode}
                                            </p>
                                        )}
                                        {/* {charLimitReached.zipCode && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctoraddress.reached_text_limit', { limit: formData.country === 'India' ? 6 : 5 })}</p>} */}
                                    </div>
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="specialty-select"
                                            id="specialty-label"
                                            data-testid="specialty-label"
                                        >
                                            {t('doctoraddress.specialities')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            {(formData.specialty.length === 0 || isSpecialtyMenuOpen) && (
                                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none">
                                                    <Search className="w-4 h-4 text-gray-400" />
                                                </div>
                                            )}
                                            <Select
                                                isMulti
                                                id="specialty-select"
                                                name="specialty"
                                                options={mappedSpecialties}
                                                value={formData.specialty.map((spec) => ({
                                                    value: spec.specialtyId,
                                                    label: spec.name,
                                                    color: spec.color,
                                                    specialtyId: spec.specialtyId,
                                                    name: spec.name,
                                                    hasTypes: Array.isArray(spec.appointmentTypes) && spec.appointmentTypes.length > 0,
                                                }))}
                                                onChange={handleSpecialityChange}
                                                onInputChange={handleSpecialtyInputChange}
                                                onFocus={() => setIsSpecialtyInputFocused(true)}
                                                onBlur={() => setIsSpecialtyInputFocused(false)}
                                                onMenuOpen={() => {
                                                    setIsSpecialtyMenuOpen(true);
                                                    setIsSpecialtyInputFocused(true);
                                                    handleSpecialtyMenuOpen();
                                                }}
                                                onMenuClose={() => {
                                                    setIsSpecialtyMenuOpen(false);
                                                    setIsSpecialtyInputFocused(false);
                                                }}
                                                styles={{
                                                    ...customSelectStyles,
                                                    placeholder: (provided) => ({
                                                        ...provided,
                                                        paddingLeft: (formData.specialty.length === 0 || isSpecialtyMenuOpen) ? '1rem' : '0.5rem',
                                                        color: '#9ca3af',
                                                    }),
                                                    input: (provided) => ({
                                                        ...provided,
                                                        paddingLeft: (formData.specialty.length === 0 || isSpecialtyMenuOpen) ? '0.5rem' : '0',
                                                        marginLeft: (formData.specialty.length === 0 || isSpecialtyMenuOpen) ? '0.5rem' : '0',
                                                    }),
                                                    valueContainer: (provided) => ({
                                                        ...provided,
                                                        paddingLeft: (formData.specialty.length === 0 || isSpecialtyMenuOpen) ? '1rem' : '0.5rem',
                                                    }),
                                                }}
                                                className="w-full rounded-xl"
                                                placeholder={t('doctoraddress.search_or_select_specialties', 'search or select specialties ...')}
                                                isDisabled={isSubmitting}
                                                isSearchable={true}
                                                isClearable={true}
                                                noOptionsMessage={() => t('doctoraddress.noOptions') || 'No options'}
                                                data-testid="specialty-select"
                                                isOptionDisabled={(option) => !option.hasTypes}

                                                /** ✅ Only show "(No appointment type)" in menu, not in selected value */
                                                formatOptionLabel={(option, { context }) =>
                                                    context === "menu" ? (
                                                        <span>
                                                            <SpecialtyOptionLabel option={option} />
                                                            {!option.hasTypes && ` (${t('doctoraddress.no_appointment_type', 'No appointment type')})`}
                                                        </span>
                                                    ) : (
                                                        <span><SpecialtyOptionLabel option={option} /></span>
                                                    )
                                                }
                                            />
                                        </div>

                                        {formErrors.specialty && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="specialty-error"
                                                data-testid="specialty-error"
                                            >
                                                {formErrors.specialty}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="doctor-address-form-cancel-button"
                                onClick={handleModalClose}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isSubmitting}
                                data-testid="cancel-button"
                            >
                                {t('doctoraddress.cancel')}
                            </button>
                            <button
                                type="submit"
                                id="doctor-address-form-submit-button"
                                form="doctor-address-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isSubmitting}
                                data-testid="submit-button"
                            >
                                {isSubmitting && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {formData.addressId ? t('doctoraddress.save') : t('doctoraddress.create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddressTab;
